/**
 * ImportPage - Page principale du module d'import
 * Wizard multi-étapes: Upload → Preview → Validation → Traitement → Résultat
 * Design V2: Glassmorphism + Teal Color Scheme (Dashboard context)
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { confirm } from '@/stores/confirmStore';
import { showSuccess, showError } from '@/lib/notifications';
import {
  Upload,
  Eye,
  CheckSquare,
  Play,
  CheckCircle,
  ArrowLeft,
  X,
  FileSpreadsheet,
  History,
  Clock,
  AlertCircle,
  Database,
  Loader2,
  ScanLine,
} from 'lucide-react';
import type {
  ImportType,
  ImportStep,
  ImportConfig,
  ValidationResult,
  Import,
} from '../types/import.types';
import {
  useImportTypes,
  useUploadImport,
  useImportPreview,
  useUpdateImportConfig,
  useValidateImport,
  useProcessImport,
  useRollbackImport,
  useImportHistory,
  useCancelImport,
} from '../hooks/useImport';
import { FileUploadZone } from './FileUploadZone';
import { ImportPreview } from './ImportPreview';
import { ImportProgress } from './ImportProgress';
import { importService } from '../services/import.service';
import { logger } from '@/utils/logger';
import { OCRInvoiceReview } from './OCRInvoiceReview';

const STEPS: { key: ImportStep; label: string; icon: typeof Upload }[] = [
  { key: 'upload', label: 'Upload', icon: Upload },
  { key: 'preview', label: 'Aperçu', icon: Eye },
  { key: 'validate', label: 'Validation', icon: CheckSquare },
  { key: 'process', label: 'Traitement', icon: Play },
  { key: 'complete', label: 'Terminé', icon: CheckCircle },
];

const IMPORT_TYPE_LABELS: Record<ImportType, string> = {
  CLIENT: 'Clients',
  PRODUCT: 'Produits',
  INVOICE: 'Factures',
  QUOTE: 'Devis',
  CREDIT_NOTE: 'Avoirs',
  EXPENSE: 'Dépenses',
  SUPPLIER_INVOICE: 'Factures fournisseurs',
  FEC: 'FEC (Fichier des Écritures Comptables)',
  BANK_STATEMENT: 'Relevés bancaires',
  BANK_TRANSACTION: 'Transactions bancaires',
  JOURNAL_ENTRY: 'Écritures comptables (Sage, Cegid, Quadra)',
  PAYMENT: 'Paiements',
};

export function ImportPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // State
  const [currentStep, setCurrentStep] = useState<ImportStep>('upload');
  const [selectedType, setSelectedType] = useState<ImportType>('FEC');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importId, setImportId] = useState<string | null>(null);
  const [config, setConfig] = useState<ImportConfig>({});
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // OCR state for PDF invoice import
  const [isOCRMode, setIsOCRMode] = useState(false);
  const [isExtractingOCR, setIsExtractingOCR] = useState(false);
  const [ocrData, setOcrData] = useState<any>(null);
  const [ocrProvider, setOcrProvider] = useState('');
  const [ocrProcessingTime, setOcrProcessingTime] = useState<number | undefined>();

  // Queries & Mutations
  const { data: typesData } = useImportTypes();
  const { data: historyData, refetch: refetchHistory } = useImportHistory({ limit: 100 });
  const uploadMutation = useUploadImport();
  const { data: previewData, isLoading: isLoadingPreview, error: previewError } = useImportPreview(importId);
  const updateConfigMutation = useUpdateImportConfig();
  const validateMutation = useValidateImport();
  const processMutation = useProcessImport();
  const rollbackMutation = useRollbackImport();
  const cancelMutation = useCancelImport();

  // Get current step index
  const currentStepIndex = STEPS.findIndex((s) => s.key === currentStep);

  // Calculate KPI stats from history
  const imports = historyData?.imports || [];
  const totalImports = imports.length;
  const pendingImports = imports.filter(i => i.status === 'PROCESSING' || i.status === 'PENDING').length;
  const completedImports = imports.filter(i => i.status === 'COMPLETED').length;
  const errorImports = imports.filter(i => i.status === 'FAILED').length;

  // Handlers
  const handleFileSelect = useCallback((file: File) => {
    setSelectedFile(file);
    setUploadError(null); // Clear previous error
  }, []);

  const handleUpload = useCallback(async () => {
    if (!selectedFile) {
      setUploadError('Veuillez sélectionner un fichier');
      return;
    }

    setUploadError(null);

    try {
      const result = await uploadMutation.mutateAsync({
        file: selectedFile,
        type: selectedType,
        config,
      });
      // Backend retourne directement { id, type, format, ... } pas { import: {...} }
      setImportId(result.id);
      setCurrentStep('preview');
    } catch (error) {
      logger.error('Upload failed:', error);
      const errorMessage = error instanceof Error ? error.message : "Erreur lors de l'upload du fichier";
      setUploadError(errorMessage);
    }
  }, [selectedFile, selectedType, config, uploadMutation]);

  // Check if current selection is INVOICE + PDF file
  const isPdfInvoice = selectedType === 'INVOICE' && selectedFile?.name?.toLowerCase().endsWith('.pdf');

  const handleOCRExtract = useCallback(async () => {
    if (!selectedFile) {
      setUploadError('Veuillez sélectionner un fichier PDF');
      return;
    }

    setUploadError(null);
    setIsExtractingOCR(true);

    try {
      const result = await importService.extractInvoiceOCR(selectedFile);
      if (result.success && result.data) {
        setOcrData(result.data.extractedData);
        setOcrProvider(result.data.provider);
        setOcrProcessingTime(result.data.processingTime);
        setIsOCRMode(true);
      } else {
        setUploadError(result.error || 'Échec de l\'extraction OCR');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur lors de l\'extraction OCR';
      setUploadError(message);
    } finally {
      setIsExtractingOCR(false);
    }
  }, [selectedFile]);

  const handleOCRInvoiceCreated = useCallback((_invoiceId: string, _invoiceNumber: string) => {
    setIsOCRMode(false);
    setOcrData(null);
    // Naviguer vers la liste des factures pour voir la facture créée
    navigate('/invoices');
  }, [navigate]);

  const handleOCRBack = useCallback(() => {
    setIsOCRMode(false);
    setOcrData(null);
  }, []);

  const handleConfigChange = useCallback(
    async (newConfig: ImportConfig) => {
      setConfig(newConfig);
      if (importId) {
        try {
          await updateConfigMutation.mutateAsync({ importId, config: newConfig });
        } catch (error) {
          logger.error('Config update failed:', error);
        }
      }
    },
    [importId, updateConfigMutation]
  );

  const handleValidate = useCallback(async () => {
    if (!importId) return;

    try {
      const result = await validateMutation.mutateAsync(importId);
      setValidationResult(result);
      setCurrentStep('validate');
    } catch (error) {
      logger.error('Validation failed:', error);
    }
  }, [importId, validateMutation]);

  const handleProcess = useCallback(async () => {
    if (!importId) {
      showError('Veuillez sélectionner un import avant de lancer le traitement.', 'Import requis');
      return;
    }

    try {
      await processMutation.mutateAsync(importId);
      setCurrentStep('process');
      showSuccess('Le traitement de l\'import a été lancé avec succès.', 'Traitement lancé');
    } catch (error) {
      logger.error('Process failed:', error);
      const message = error instanceof Error ? error.message : 'Une erreur est survenue lors du lancement du traitement';
      showError(message, 'Échec du traitement');
    }
  }, [importId, processMutation]);

  const handleComplete = useCallback((_importData: Import) => {
    setCurrentStep('complete');
  }, []);

  const handleDownload = useCallback(async () => {
    if (!importId) return;

    try {
      const blob = await importService.downloadFile(importId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = selectedFile?.name || 'import-file';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      logger.error('Download failed:', error);
    }
  }, [importId, selectedFile]);

  const handleRollback = useCallback(async () => {
    if (!importId) return;

    const confirmed = await confirm({
      title: 'Annuler cet import ?',
      message: 'Cette action supprimera toutes les données importées. Cette opération est irréversible.',
      confirmText: 'Oui, annuler l\'import',
      cancelText: 'Non, conserver',
      variant: 'danger',
    });

    if (!confirmed) return;

    try {
      await rollbackMutation.mutateAsync(importId);
      showSuccess('L\'import a été annulé et les données supprimées.', 'Import annulé');
      navigate('/imports');
    } catch (error) {
      logger.error('Rollback failed:', error);
      const message = error instanceof Error ? error.message : 'Une erreur est survenue lors de l\'annulation';
      showError(message, 'Échec de l\'annulation');
    }
  }, [importId, rollbackMutation, navigate]);

  const handleNewImport = useCallback(() => {
    setCurrentStep('upload');
    setSelectedFile(null);
    setImportId(null);
    setConfig({});
    setValidationResult(null);
    setUploadError(null);
    setIsOCRMode(false);
    setOcrData(null);
  }, []);

  const handleDeleteImport = useCallback(async (id: string) => {
    const confirmed = await confirm({
      title: t('import.confirm.deleteTitle'),
      message: t('import.confirm.deleteMessage'),
      confirmText: t('import.confirm.deleteConfirm'),
      cancelText: t('import.confirm.deleteCancel'),
      variant: 'danger',
    });

    if (!confirmed) return;

    try {
      await cancelMutation.mutateAsync(id);
      showSuccess(t('import.notifications.importDeleted'), t('import.notifications.importDeletedTitle'));
      refetchHistory();
    } catch (error) {
      logger.error('Delete failed:', error);
      const message = error instanceof Error ? error.message : t('import.notifications.deleteFailed');
      showError(message, t('import.notifications.deleteFailed'));
    }
  }, [cancelMutation, refetchHistory, t]);

  const handleResumeImport = useCallback((imp: Import) => {
    setImportId(imp.id);
    setSelectedType(imp.type);
    // Déterminer l'étape appropriée selon le statut
    if (imp.status === 'VALIDATED' || imp.status === 'AWAITING_CONFIRM') {
      // Import déjà validé → passer directement à l'étape de traitement
      setCurrentStep('process');
    } else {
      // Import uploadé, parsé ou autre → étape aperçu
      // (PARSED va aussi à preview car validationResult n'est pas persisté côté client)
      setCurrentStep('preview');
    }
  }, []);

  // Render step content
  const renderStepContent = () => {
    // OCR Review Mode
    if (isOCRMode && ocrData) {
      return (
        <OCRInvoiceReview
          extractedData={ocrData}
          provider={ocrProvider}
          processingTime={ocrProcessingTime}
          onBack={handleOCRBack}
          onInvoiceCreated={handleOCRInvoiceCreated}
        />
      );
    }

    switch (currentStep) {
      case 'upload':
        return (
          <div className="space-y-6">
            {/* Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type d'import
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value as ImportType)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-gray-900 bg-white focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-300"
              >
                {typesData?.types && typesData.types.length > 0
                  ? typesData.types.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))
                  : Object.entries(IMPORT_TYPE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))
                }
              </select>
            </div>

            {/* FEC-specific options */}
            {selectedType === 'FEC' && (
              <div className="p-5 bg-white/40 backdrop-blur-sm border border-white/50 rounded-2xl space-y-4">
                <h4 className="font-medium text-gray-900 flex items-center gap-2">
                  <Database className="w-4 h-4 text-teal-600" />
                  Options FEC
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={config.fecOptions?.createMissingAccounts ?? true}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          fecOptions: {
                            ...config.fecOptions,
                            createMissingAccounts: e.target.checked,
                          },
                        })
                      }
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors">Créer les comptes manquants</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={config.fecOptions?.preserveReconciliation ?? true}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          fecOptions: {
                            ...config.fecOptions,
                            preserveReconciliation: e.target.checked,
                          },
                        })
                      }
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors">Préserver le lettrage</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={config.fecOptions?.skipInvalidLines ?? false}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          fecOptions: {
                            ...config.fecOptions,
                            skipInvalidLines: e.target.checked,
                          },
                        })
                      }
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors">Ignorer les lignes invalides</span>
                  </label>
                </div>
              </div>
            )}

            {/* JOURNAL_ENTRY options (Sage PNM, Cegid TRA, Quadratus) */}
            {selectedType === 'JOURNAL_ENTRY' && (
              <div className="p-5 bg-white/40 backdrop-blur-sm border border-white/50 rounded-2xl space-y-4">
                <h4 className="font-medium text-gray-900 flex items-center gap-2">
                  <Database className="w-4 h-4 text-teal-600" />
                  Options d'import comptable
                </h4>
                <p className="text-sm text-gray-500">
                  Formats supportés: Sage 100 (.pnm), Cegid Expert (.tra), Quadratus (.txt)
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={config.sagePNMOptions?.createMissingAccounts ?? config.cegidTRAOptions?.createMissingAccounts ?? config.quadraOptions?.createMissingAccounts ?? true}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          sagePNMOptions: { ...config.sagePNMOptions, createMissingAccounts: e.target.checked },
                          cegidTRAOptions: { ...config.cegidTRAOptions, createMissingAccounts: e.target.checked },
                          quadraOptions: { ...config.quadraOptions, createMissingAccounts: e.target.checked },
                        })
                      }
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors">Créer les comptes manquants</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={config.sagePNMOptions?.preserveReconciliation ?? config.cegidTRAOptions?.preserveReconciliation ?? config.quadraOptions?.preserveReconciliation ?? true}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          sagePNMOptions: { ...config.sagePNMOptions, preserveReconciliation: e.target.checked },
                          cegidTRAOptions: { ...config.cegidTRAOptions, preserveReconciliation: e.target.checked },
                          quadraOptions: { ...config.quadraOptions, preserveReconciliation: e.target.checked },
                        })
                      }
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors">Préserver le lettrage</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={config.sagePNMOptions?.skipInvalidLines ?? config.cegidTRAOptions?.skipInvalidLines ?? config.quadraOptions?.skipInvalidLines ?? false}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          sagePNMOptions: { ...config.sagePNMOptions, skipInvalidLines: e.target.checked },
                          cegidTRAOptions: { ...config.cegidTRAOptions, skipInvalidLines: e.target.checked },
                          quadraOptions: { ...config.quadraOptions, skipInvalidLines: e.target.checked },
                        })
                      }
                      className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors">Ignorer les lignes invalides</span>
                  </label>
                </div>
              </div>
            )}

            {/* File Upload */}
            <FileUploadZone
              onFileSelect={handleFileSelect}
              selectedType={selectedType}
              acceptedFormats={
                selectedType === 'FEC'
                  ? ['.txt', '.csv', '.fec']
                  : selectedType === 'JOURNAL_ENTRY'
                  ? ['.txt', '.pnm', '.tra', '.csv']
                  : selectedType === 'BANK_STATEMENT'
                  ? ['.csv', '.xlsx', '.xls', '.ofx', '.qif', '.mt940', '.sta', '.pdf']
                  : selectedType === 'BANK_TRANSACTION'
                  ? ['.csv', '.xlsx', '.xls', '.ofx', '.qif', '.mt940', '.sta']
                  : selectedType === 'SUPPLIER_INVOICE'
                  ? ['.csv', '.xlsx', '.xls', '.pdf', '.xml']
                  : selectedType === 'INVOICE'
                  ? ['.csv', '.xlsx', '.xls', '.txt', '.pdf']
                  : ['.csv', '.xlsx', '.xls', '.txt']
              }
              disabled={uploadMutation.isPending}
            />

            {/* OCR Extraction for PDF Invoices */}
            {isPdfInvoice && selectedFile && (
              <div className="bg-teal-50/50 border border-teal-200 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <ScanLine className="w-5 h-5 text-teal-600" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      Fichier PDF détecté — Import via OCR
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      L'intelligence artificielle va extraire automatiquement les données de la facture
                    </p>
                  </div>
                  <button
                    onClick={handleOCRExtract}
                    disabled={isExtractingOCR}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
                  >
                    {isExtractingOCR ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Extraction...
                      </>
                    ) : (
                      <>
                        <ScanLine className="w-4 h-4" />
                        Extraire via OCR
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Upload Error */}
            {uploadError && (
              <div className="flex items-center gap-3 p-4 bg-red-50/80 border border-red-200/50 rounded-xl text-red-700">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span>{uploadError}</span>
              </div>
            )}

            {/* Upload Button — masqué pour les PDF factures (flow OCR) */}
            {!isPdfInvoice && (
              <div className="flex justify-end">
                <button
                  onClick={handleUpload}
                  disabled={!selectedFile || uploadMutation.isPending}
                  className="flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-xl font-medium hover:bg-teal-700 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none"
                >
                  {uploadMutation.isPending ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Upload en cours...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      Charger le fichier
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        );

      case 'preview':
        return (
          <div className="space-y-6">
            {isLoadingPreview ? (
              <div className="flex items-center justify-center p-12">
                <div className="w-8 h-8 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : previewData ? (
              <>
                <ImportPreview
                  preview={previewData}
                  config={config}
                  onConfigChange={handleConfigChange}
                  importId={importId || undefined}
                />
                <div className="flex justify-between">
                  <button
                    onClick={() => setCurrentStep('upload')}
                    className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-white/50 rounded-xl transition-all duration-300"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    Retour
                  </button>
                  <button
                    onClick={handleValidate}
                    disabled={validateMutation.isPending}
                    className="flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-xl font-medium hover:bg-teal-700 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:hover:translate-y-0"
                  >
                    {validateMutation.isPending ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Validation...
                      </>
                    ) : (
                      <>
                        <CheckSquare className="w-5 h-5" />
                        Valider les données
                      </>
                    )}
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center py-12 space-y-4">
                <div className="flex flex-col items-center gap-3">
                  <AlertCircle className="w-12 h-12 text-red-400" />
                  <p className="text-gray-700 font-medium">Impossible de charger l'aperçu des données</p>
                  {previewError && (
                    <p className="text-sm text-red-500 max-w-md">
                      {previewError instanceof Error
                        ? previewError.message
                        : typeof previewError === 'object' && previewError !== null && 'message' in previewError
                        ? String((previewError as { message: unknown }).message)
                        : 'Erreur lors du chargement'}
                    </p>
                  )}
                  <p className="text-sm text-gray-500">
                    Le fichier source n'est peut-être plus disponible. Vous pouvez re-uploader le fichier.
                  </p>
                </div>
                <button
                  onClick={handleNewImport}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-xl font-medium hover:bg-teal-700 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
                >
                  <Upload className="w-5 h-5" />
                  Re-uploader un fichier
                </button>
              </div>
            )}
          </div>
        );

      case 'validate':
        return (
          <div className="space-y-6">
            {validationResult && (
              <>
                {/* Validation Summary */}
                <div
                  className={`p-6 rounded-2xl backdrop-blur-sm border ${
                    validationResult.valid
                      ? 'bg-green-50/80 border-green-200/50'
                      : 'bg-red-50/80 border-red-200/50'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    {validationResult.valid ? (
                      <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                        <CheckCircle className="w-6 h-6 text-green-500" />
                      </div>
                    ) : (
                      <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                        <X className="w-6 h-6 text-red-500" />
                      </div>
                    )}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {validationResult.valid ? 'Validation réussie' : 'Erreurs détectées'}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {validationResult.validCount} lignes valides,{' '}
                        {validationResult.errorCount} erreurs,{' '}
                        {validationResult.warningCount} avertissements
                      </p>
                    </div>
                  </div>
                </div>

                {/* Errors List */}
                {validationResult.errors.length > 0 && (
                  <div className="bg-white/40 backdrop-blur-sm border border-red-200/50 rounded-2xl overflow-hidden">
                    <div className="bg-red-50/50 px-5 py-3 border-b border-red-200/50">
                      <h4 className="font-medium text-red-800 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Erreurs ({validationResult.errors.length})
                      </h4>
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                      {validationResult.errors.slice(0, 20).map((error, index) => (
                        <div
                          key={index}
                          className="px-5 py-3 border-b border-red-100/50 last:border-b-0 hover:bg-white/30 transition-colors"
                        >
                          <span className="text-xs text-red-600 font-mono bg-red-100/50 px-2 py-0.5 rounded">
                            Ligne {error.rowNumber}
                          </span>
                          <ul className="text-sm text-red-700 ml-4 mt-1 space-y-0.5">
                            {error.errors.map((e, i) => (
                              <li key={i}>
                                <span className="font-medium">{e.field}:</span> {e.message}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                      {validationResult.errors.length > 20 && (
                        <div className="px-5 py-3 text-xs text-red-500 text-center bg-red-50/30">
                          +{validationResult.errors.length - 20} erreurs supplémentaires
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Warnings List */}
                {validationResult.warnings.length > 0 && (
                  <div className="bg-white/40 backdrop-blur-sm border border-amber-200/50 rounded-2xl overflow-hidden">
                    <div className="bg-amber-50/50 px-5 py-3 border-b border-amber-200/50">
                      <h4 className="font-medium text-amber-800 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Avertissements ({validationResult.warnings.length})
                      </h4>
                    </div>
                    <div className="max-h-40 overflow-y-auto">
                      {validationResult.warnings.slice(0, 10).map((warning, index) => (
                        <div
                          key={index}
                          className="px-5 py-3 border-b border-amber-100/50 last:border-b-0 text-sm text-amber-700 hover:bg-white/30 transition-colors"
                        >
                          <span className="font-mono text-xs bg-amber-100/50 px-2 py-0.5 rounded">Ligne {warning.rowNumber}</span>
                          <span className="ml-2">{warning.warnings.map((w) => w.message).join(', ')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-between">
                  <button
                    onClick={() => setCurrentStep('preview')}
                    className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-white/50 rounded-xl transition-all duration-300"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    Retour
                  </button>
                  <button
                    onClick={handleProcess}
                    disabled={!validationResult.valid || processMutation.isPending}
                    className="flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-xl font-medium hover:bg-teal-700 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none"
                  >
                    {processMutation.isPending ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Lancement...
                      </>
                    ) : (
                      <>
                        <Play className="w-5 h-5" />
                        Lancer l'import
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        );

      case 'process':
      case 'complete':
        return (
          <div className="space-y-6">
            {importId && (
              <ImportProgress
                importId={importId}
                onComplete={handleComplete}
                onDownload={handleDownload}
                onRollback={handleRollback}
                onStartProcess={handleProcess}
                isStartingProcess={processMutation.isPending}
              />
            )}
            {currentStep === 'complete' && (
              <div className="flex justify-center gap-4">
                <button
                  onClick={handleNewImport}
                  className="flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-xl font-medium hover:bg-teal-700 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
                >
                  <Upload className="w-5 h-5" />
                  Nouvel import
                </button>
                <button
                  onClick={() => navigate('/imports')}
                  className="flex items-center gap-2 px-6 py-3 bg-white/50 hover:bg-white/70 text-gray-700 rounded-xl font-medium border border-gray-200/50 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
                >
                  <History className="w-5 h-5" />
                  Historique
                </button>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="px-8 pt-8 pb-12 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-teal-100 rounded-2xl flex items-center justify-center">
            <FileSpreadsheet className="w-7 h-7 text-teal-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Import de données</h1>
            <p className="text-gray-500">
              Importez vos données depuis CSV, Excel, FEC, Sage, Cegid ou Quadratus
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate('/imports')}
          className="flex items-center gap-2 px-4 py-2 bg-white/50 hover:bg-white/70 text-gray-700 rounded-xl font-medium border border-gray-200/50 transition-all duration-300"
        >
          <History className="w-5 h-5" />
          Historique
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white/20 backdrop-blur-xl rounded-3xl p-6 border border-white/30 shadow-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
              <Database className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total imports</p>
              <p className="text-2xl font-bold text-gray-900">{totalImports}</p>
            </div>
          </div>
        </div>

        <div className="bg-white/20 backdrop-blur-xl rounded-3xl p-6 border border-white/30 shadow-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">En cours</p>
              <p className="text-2xl font-bold text-gray-900">{pendingImports}</p>
            </div>
          </div>
        </div>

        <div className="bg-white/20 backdrop-blur-xl rounded-3xl p-6 border border-white/30 shadow-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Réussis</p>
              <p className="text-2xl font-bold text-green-600">{completedImports}</p>
            </div>
          </div>
        </div>

        <div className="bg-white/20 backdrop-blur-xl rounded-3xl p-6 border border-white/30 shadow-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Échecs</p>
              <p className="text-2xl font-bold text-red-600">{errorImports}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stepper */}
      <div className="flex flex-wrap items-center justify-center gap-2">
        {STEPS.map((step, index) => {
          const StepIcon = step.icon;
          const isActive = index === currentStepIndex;
          const isCompleted = index < currentStepIndex;

          return (
            <div key={step.key} className="flex items-center">
              <button
                disabled
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  isActive
                    ? 'bg-teal-600 text-white shadow-lg'
                    : isCompleted
                    ? 'bg-green-100 text-green-700'
                    : 'bg-white/50 text-gray-400'
                }`}
              >
                <StepIcon className="w-4 h-4" />
                <span className="hidden md:inline">{step.label}</span>
              </button>
              {index < STEPS.length - 1 && (
                <div
                  className={`w-8 h-0.5 mx-1 transition-colors duration-300 ${
                    isCompleted ? 'bg-green-300' : 'bg-gray-200'
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Content Card */}
      <div className="bg-white/20 backdrop-blur-xl rounded-3xl p-6 md:p-8 border border-white/30 shadow-xl">
        {renderStepContent()}
      </div>

      {/* Historique des imports */}
      {imports.length > 0 && (
        <div className="bg-white/20 backdrop-blur-xl rounded-3xl p-6 md:p-8 border border-white/30 shadow-xl">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <History className="w-5 h-5 text-teal-600" />
            Historique des imports
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50/50">
                <tr>
                  <th className="px-4 py-3 text-left text-gray-700 font-medium">Date</th>
                  <th className="px-4 py-3 text-left text-gray-700 font-medium">Type</th>
                  <th className="px-4 py-3 text-left text-gray-700 font-medium">Fichier</th>
                  <th className="px-4 py-3 text-left text-gray-700 font-medium">Lignes</th>
                  <th className="px-4 py-3 text-left text-gray-700 font-medium">Statut</th>
                  <th className="px-4 py-3 text-left text-gray-700 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {imports.slice(0, 10).map((imp: Import) => (
                  <tr key={imp.id} className="hover:bg-gray-50/50">
                    <td className="px-4 py-3 text-gray-900">
                      {new Date(imp.createdAt).toLocaleDateString('fr-FR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-teal-100 text-teal-700 rounded-lg text-xs font-medium">
                        {IMPORT_TYPE_LABELS[imp.type] || imp.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-900 truncate max-w-[200px]" title={imp.originalFileName}>
                      {imp.originalFileName}
                    </td>
                    <td className="px-4 py-3 text-gray-900">
                      {imp.totalRows || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${
                          imp.status === 'COMPLETED' || imp.status === 'COMPLETED_PARTIAL'
                            ? 'bg-green-100 text-green-700'
                            : imp.status === 'FAILED' || imp.status === 'CANCELLED'
                            ? 'bg-red-100 text-red-700'
                            : imp.status === 'PROCESSING'
                            ? 'bg-yellow-100 text-yellow-700'
                            : imp.status === 'PARSED' || imp.status === 'VALIDATED' || imp.status === 'UPLOADED'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {(imp.status === 'COMPLETED' || imp.status === 'COMPLETED_PARTIAL') && <CheckCircle className="w-3 h-3" />}
                        {(imp.status === 'FAILED' || imp.status === 'CANCELLED') && <AlertCircle className="w-3 h-3" />}
                        {imp.status === 'PROCESSING' && <Clock className="w-3 h-3" />}
                        {imp.status === 'UPLOADED' && <Upload className="w-3 h-3" />}
                        {imp.status === 'COMPLETED'
                          ? 'Terminé'
                          : imp.status === 'COMPLETED_PARTIAL'
                          ? 'Terminé (partiel)'
                          : imp.status === 'FAILED'
                          ? 'Échec'
                          : imp.status === 'CANCELLED'
                          ? 'Annulé'
                          : imp.status === 'PROCESSING'
                          ? 'En cours'
                          : imp.status === 'UPLOADED'
                          ? 'Uploadé'
                          : imp.status === 'PARSED'
                          ? 'Parsé'
                          : imp.status === 'VALIDATED'
                          ? 'Validé'
                          : imp.status === 'PENDING'
                          ? 'En attente'
                          : imp.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {(imp.status === 'UPLOADED' || imp.status === 'PARSED' || imp.status === 'VALIDATED' || imp.status === 'PENDING') && (
                          <button
                            onClick={() => handleResumeImport(imp)}
                            className="flex items-center gap-1 px-2 py-1 bg-teal-100 text-teal-700 rounded-lg text-xs font-medium hover:bg-teal-200 transition-colors"
                          >
                            <Eye className="w-3 h-3" />
                            Reprendre
                          </button>
                        )}
                        {(imp.status === 'COMPLETED' || imp.status === 'COMPLETED_PARTIAL') && (
                          <button
                            onClick={() => {
                              setImportId(imp.id);
                              setSelectedType(imp.type);
                              setCurrentStep('complete');
                            }}
                            className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-lg text-xs font-medium hover:bg-green-200 transition-colors"
                          >
                            <Eye className="w-3 h-3" />
                            Voir
                          </button>
                        )}
                        {imp.status === 'FAILED' && (
                          <span className="text-xs text-red-500 truncate max-w-[150px]" title={imp.errorMessage || ''}>
                            {imp.errorMessage || 'Erreur inconnue'}
                          </span>
                        )}
                        {/* Bouton Annuler/Supprimer - selon le statut */}
                        {imp.status !== 'PROCESSING' && (
                          <button
                            onClick={() => handleDeleteImport(imp.id)}
                            disabled={cancelMutation.isPending}
                            className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-lg text-xs font-medium hover:bg-red-200 transition-colors disabled:opacity-50"
                            title={['CANCELLED', 'FAILED', 'COMPLETED', 'COMPLETED_PARTIAL'].includes(imp.status)
                              ? "Supprimer de l'historique"
                              : "Annuler cet import"}
                          >
                            <X className="w-3 h-3" />
                            {['CANCELLED', 'FAILED', 'COMPLETED', 'COMPLETED_PARTIAL'].includes(imp.status)
                              ? 'Supprimer'
                              : 'Annuler'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {imports.length > 10 && (
            <p className="text-xs text-gray-500 mt-3 text-center">
              Affichage des 10 derniers imports sur {imports.length}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
